import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select
} from "@material-ui/core";
import { DEVICE_STATUS } from "../../../domain/device";
import I18nText from "../../../elements/I18nText";
import { useTranslation } from "../../../i18n";

const STATUS_OPTIONS = Object.values(DEVICE_STATUS);

// Forces the menu to always open directly below the field
const MENU_ANCHOR_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null
};

// Fixed cap for the menu paper
const MENU_PAPER_MAX_HEIGHT = 320;

const MenuPaperWithApply = React.forwardRef(function MenuPaperWithApply(
  { children, onApply, applyLabel, onClear, clearLabel, style, ...other },
  ref
) {
  return (
    <Paper
      ref={ref}
      style={{
        ...style,
        maxHeight: MENU_PAPER_MAX_HEIGHT,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
      {...other}
    >
      <Box flex="1 1 auto" minHeight={0} overflow="auto">
        {children}
      </Box>
      <Divider />
      <Box
        display="flex"
        justifyContent="flex-end"
        p={1}
        gridGap={8}
        flexShrink={0}
      >
        <Button size="small" onClick={onClear}>
          {clearLabel}
        </Button>
        <Button size="small" color="primary" onClick={onApply}>
          {applyLabel}
        </Button>
      </Box>
    </Paper>
  );
});

const AdvancedFilters = ({
  filters,
  onFilterChange,
  options = {},
  contentTypes = []
}) => {
  const { t } = useTranslation();
  const {
    mediumOptions = contentTypes,
    projectOptions = [],
    ownerOptions = []
  } = options;

  const [openSelect, setOpenSelect] = useState(null);
  const [draftValue, setDraftValue] = useState([]);

  const openMultiSelect = (key, currentValue) => {
    setOpenSelect(key);
    setDraftValue(currentValue);
  };

  const applyMultiSelect = key => {
    onFilterChange(key, draftValue);
    setOpenSelect(null);
  };

  const clearMultiSelect = key => {
    onFilterChange(key, []);
    setOpenSelect(null);
  };

  const multiSelectMenuProps = key => ({
    ...MENU_ANCHOR_PROPS,
    PaperProps: {
      component: MenuPaperWithApply,
      onApply: () => applyMultiSelect(key),
      applyLabel: t("topbar.filter.apply"),
      onClear: () => clearMultiSelect(key),
      clearLabel: t("topbar.filter.clear")
    }
  });

  return (
    <Box display="flex" flexDirection="column" gridGap={16} mt={2}>
      <FormControl variant="outlined" size="small" fullWidth margin="dense">
        <InputLabel id="medium-label">Medium</InputLabel>
        <Select
          labelId="medium-label"
          value={filters.medium}
          onChange={e => onFilterChange("medium", e.target.value)}
          label="Medium"
          MenuProps={MENU_ANCHOR_PROPS}
        >
          <MenuItem value="">
            <em>
              <I18nText>topbar.filter.any</I18nText>
            </em>
          </MenuItem>
          {mediumOptions.map(opt => (
            <MenuItem key={String(opt.value ?? opt)} value={opt.value ?? opt}>
              <I18nText>{`sensors.contentTypes.${opt}`}</I18nText>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {projectOptions.length > 0 && (
        <FormControl variant="outlined" size="small" fullWidth margin="dense">
          <InputLabel id="project-label">
            {t("topbar.filter.project")}
          </InputLabel>
          <Select
            labelId="project-label"
            multiple
            open={openSelect === "project"}
            onOpen={() => openMultiSelect("project", filters.project)}
            onClose={() => setOpenSelect(null)}
            value={openSelect === "project" ? draftValue : filters.project}
            onChange={e => setDraftValue(e.target.value)}
            label={t("topbar.filter.project")}
            renderValue={selected => selected.join(", ")}
            MenuProps={multiSelectMenuProps("project")}
          >
            {projectOptions.map(project => (
              <MenuItem key={project} value={project}>
                <Checkbox
                  checked={
                    (openSelect === "project"
                      ? draftValue
                      : filters.project
                    ).indexOf(project) !== -1
                  }
                />
                <ListItemText primary={project} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {ownerOptions.length > 0 && (
        <FormControl variant="outlined" size="small" fullWidth margin="dense">
          <InputLabel id="owner-label">{t("topbar.filter.owner")}</InputLabel>
          <Select
            labelId="owner-label"
            multiple
            open={openSelect === "owner"}
            onOpen={() => openMultiSelect("owner", filters.ownerId)}
            onClose={() => setOpenSelect(null)}
            value={openSelect === "owner" ? draftValue : filters.ownerId}
            onChange={e => setDraftValue(e.target.value)}
            label={t("topbar.filter.owner")}
            renderValue={selected =>
              selected
                .map(
                  id => ownerOptions.find(owner => owner.id === id)?.username
                )
                .filter(Boolean)
                .join(", ")
            }
            MenuProps={multiSelectMenuProps("ownerId")}
          >
            {ownerOptions.map(owner => (
              <MenuItem key={owner.id} value={owner.id}>
                <Checkbox
                  checked={
                    (openSelect === "owner"
                      ? draftValue
                      : filters.ownerId
                    ).indexOf(owner.id) !== -1
                  }
                />
                <ListItemText primary={owner.username} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl variant="outlined" size="small" fullWidth margin="dense">
        <InputLabel id="status-label">{t("topbar.filter.status")}</InputLabel>
        <Select
          labelId="status-label"
          multiple
          open={openSelect === "status"}
          onOpen={() => openMultiSelect("status", filters.status)}
          onClose={() => setOpenSelect(null)}
          value={openSelect === "status" ? draftValue : filters.status}
          onChange={e => setDraftValue(e.target.value)}
          label={t("topbar.filter.status")}
          renderValue={selected =>
            selected
              .map(status => t(`devices.common.statuses.${status}`))
              .join(", ")
          }
          MenuProps={multiSelectMenuProps("status")}
        >
          {STATUS_OPTIONS.map(status => (
            <MenuItem key={status} value={status}>
              <Checkbox
                checked={
                  (openSelect === "status"
                    ? draftValue
                    : filters.status
                  ).indexOf(status) !== -1
                }
              />
              <ListItemText primary={t(`devices.common.statuses.${status}`)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default AdvancedFilters;
